/**
 * Raw devices, resolved into the game's action set.
 *
 * Pure: handed a set of held key codes and a set of held mouse buttons, it
 * hands back `{ move, actions }`. Which key and which button is data, in
 * `bindings.json`, so a remap is an edit to a config file rather than a hunt
 * through the movement code.
 *
 * Keyboard and mouse only. The controller is resolved the same way, into the
 * same verbs, in `gamepad.js`, which is where its deadzones and axes live;
 * here a digital key is the only source of the move vector, so the only
 * analogue problem left is normalising the diagonals. Hold W and D without
 * that and you walk 1.41x faster than either one alone.
 *
 * +y is forward (away from the camera) and +x is right. Mouse look is *not*
 * here; a look delta is a stream of events, not a state you can resolve from a
 * held set, so it lives in `input.js` with the rest of the device mess.
 */

export class InputConfigError extends Error {}

/** The verbs the game reasons about. Anything else is a key, not an action. */
/**
 * The verbs the game reasons about — all six of them, and the list is the point.
 *
 * It used to be sixteen. Crouch, consume, charm and three numbered deal options
 * were each a key that meant one thing in one place and nothing anywhere else,
 * and between them they put six bindings in front of a player for verbs they
 * might use twice a shift. Every one of those is now a candidate the action key
 * offers or the hold fans out, which is where a rare verb belongs: nothing to
 * memorise, nothing unreachable, and the screen says which before you press it.
 * See `prompts.js`.
 *
 * What is left is movement, the three gestures, and the two dev keys:
 *
 *   `interact`  the action key. Tapped it does the one thing on screen; held it
 *               offers everything else available where you are standing.
 *   `sheet`     the hold key. The BEO in front of your face; the action key
 *               turns it over to your card while it is up. That was Q once,
 *               a key that did one thing only while another was held, and
 *               the action key is free while your hands are full of paper.
 *   `throw`     held to wind up, released to let go. An analogue charge rather
 *               than an action: how hard is a number, and a number needs a
 *               hold of its own. Putting something *down* is in the fan.
 *   `submit`    files the room, and does nothing else anywhere.
 *
 * That last one went into the fan for one commit and it was wrong. It is the
 * most-pressed deliberate act in the game and the end of every event, and a
 * verb you use forty times a week does not belong behind a gesture you use for
 * the ones you use twice. It is not in the fan and it is not on the action key,
 * which is the point: with a key of its own it cannot eat the placement press
 * and the Banquet Captain wandering past cannot eat it.
 */
export const ACTIONS = Object.freeze([
  'sprint',
  'jump',
  'interact',
  'sheet',
  'throw',
  'submit',
  /*
   * `snap` sets what you are carrying at the nearest spot on the event's plan
   * within reach, through the same placement the action key uses, so it
   * cannot set anything the key could not; it saves lining up by hand.
   */
  'snap',
  /*
   * And the keys that are not verbs.
   *
   * The controls card, the debug panel, the time scale and the volume. None of
   * them does anything in the world, none of them can be offered by the action
   * key or the fan, and
   * a player who never touches one plays exactly the same game. They are here
   * rather than on a raw listener so that every key this page swallows is
   * written down in one file, which is the whole reason `bindings.json` exists.
   */
  'controls',
  /** The route line on the floor and the map to the objective, on or off. */
  'route',
  /** "What do I do?": the goal, where it is, and the buttons it needs. */
  'help',
  'debug',
  'timeUp',
  'timeDown',
  'mute',
  'louder',
  'quieter',
]);

/** The four cardinals the move vector is assembled from. */
export const DIRECTIONS = Object.freeze(['forward', 'back', 'left', 'right']);

const AXIS = Object.freeze({
  forward: { x: 0, y: 1 },
  back: { x: 0, y: -1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
});

const NOTHING = Object.freeze({ move: Object.freeze({ x: 0, y: 0 }), actions: Object.freeze([]) });

/** Nothing held. Shared, so a frame with no input allocates nothing. */
export const idleInput = () => NOTHING;

/** Throws on a bindings file that cannot be satisfied, so a typo fails at boot. */
export function validateBindings(bindings) {
  const { keyboard, mouse } = bindings ?? {};
  if (!keyboard || typeof keyboard !== 'object') {
    throw new InputConfigError('bindings.keyboard must be an object');
  }
  if (!mouse || typeof mouse !== 'object') {
    throw new InputConfigError('bindings.mouse must be an object');
  }

  const expected = [...DIRECTIONS, ...ACTIONS];
  for (const name of expected) {
    if (!Array.isArray(keyboard[name]) || keyboard[name].length === 0) {
      throw new InputConfigError(`keyboard binding "${name}" needs at least one key code`);
    }
  }
  for (const name of Object.keys(keyboard)) {
    if (!expected.includes(name)) {
      throw new InputConfigError(`keyboard binding "${name}" is not an action or a direction`);
    }
  }
  for (const [name, buttons] of Object.entries(mouse)) {
    if (name === 'sensitivity' || name === 'invertY') continue;
    if (!expected.includes(name)) {
      throw new InputConfigError(`mouse binding "${name}" is not an action or a direction`);
    }
    if (!Array.isArray(buttons) || buttons.some((b) => !Number.isInteger(b) || b < 0)) {
      throw new InputConfigError(`mouse binding "${name}" needs a button index array`);
    }
  }
  if (!(mouse.sensitivity > 0)) {
    throw new InputConfigError('mouse.sensitivity must be positive');
  }
  return bindings;
}

/** Every key code the bindings claim, so the caller knows what to swallow. */
export function boundKeys(bindings) {
  return new Set(Object.values(bindings.keyboard).flat());
}

/** The action state implied by a set of held key codes and mouse buttons. */
export function resolve(bindings, keys, buttons = new Set()) {
  const { keyboard, mouse } = bindings;
  const down = (name) =>
    keyboard[name].some((code) => keys.has(code)) ||
    (mouse[name] ?? []).some((index) => buttons.has(index));

  let x = 0;
  let y = 0;
  for (const direction of DIRECTIONS) {
    if (!down(direction)) continue;
    x += AXIS[direction].x;
    y += AXIS[direction].y;
  }

  return { move: clampToUnit({ x, y }), actions: ACTIONS.filter(down) };
}

/** Shrinks a vector back to unit length, but never stretches a short one. */
function clampToUnit(v) {
  const length = Math.hypot(v.x, v.y);
  return length > 1 ? { x: v.x / length, y: v.y / length } : v;
}
